import amber from '@mui/material/colors/amber';
import blue from '@mui/material/colors/blue';
import green from '@mui/material/colors/green';
import grey from '@mui/material/colors/grey';
import indigo from '@mui/material/colors/indigo';
import orange from '@mui/material/colors/orange';
import red from '@mui/material/colors/red';
import * as locales from '@mui/material/locale';
import { createTheme, darken, ThemeOptions } from '@mui/material/styles';

const defaultTheme: ThemeOptions = {
    palette: {
        primary: {
            main: "#333F50",
            dark: "#222a35",
            light: "#71767d",
        },

        secondary: {
            light: "#5393ff",
            main: darken("#5393ff", 0.3),
            dark: darken("#5393ff", 0.6),
        },
    },

    genie: {
        font: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 300,
            lineHeight: "1.5em"
        },

        boxShadow: {

            default: {
                border: 0,
                borderRadius: 3,
                boxShadow: "0 10px 20px -12px rgba(0, 0, 0, 0.42), 0 3px 20px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2)",
                padding: "10px 0",
                transition: "all 150ms ease 0s"
            },

            primary: "0 12px 20px -10px rgba(39, 98, 176, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(39, 75, 176, 0.2)",

            secondary: "none",

            error: "0 12px 20px -10px rgba(244, 67, 54, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(244, 67, 54, 0.2)",
        },

        palette: {
            pressed: {
                main: blue[400],
                dark: blue[600],
                light: blue[200],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(255, 152, 0, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(255, 152, 0, 0.2)"
            },

            notice: {
                main: "#FFF8E5",
                dark: "#FFF2CC",
                light: "#FFFAEB",
                contrastText: "#5B9BD5",
                boxShadow: "0 12px 20px -10px rgba(255, 230, 160, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(255, 230, 160, 0.2)"
            },

            warning: {
                main: orange[400],
                dark: orange[600],
                light: orange[200],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(255, 152, 0, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(255, 152, 0, 0.2)"

            },
            info: {
                main: blue[500],
                dark: blue[900],
                light: blue[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(0, 188, 212, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(0, 188, 212, 0.2)"
            },
            success: {
                main: green[500],
                dark: green[900],
                light: green[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(76, 175, 80, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(76, 175, 80, 0.2)"
            },
            muted: {
                main: grey[500],
                dark: grey[900],
                light: grey[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(76, 175, 80, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(76, 175, 80, 0.2)"
            },
        },

        sideMenu: {
            activeTextColor: "#fff",
            backgroundColor: "#1c54b2",
            borderLeftColor: "#5393ff",
            floatParcialRouteTextColor: "#1c54b2",
            floatBackgroundColor: "#5393ff",
            linkColor: indigo[500],
            textColor: "#7f7f7f",
        },

        icon: {
            action: {
                color: "#909090",
            },
        },

        user: {
            ordinary: {
                styles: {
                    color: "#808080",
                    fontWeight: "bold",
                    textDecoration: "none"
                }
            },

            sysUser: {
                styles: {
                    color: "#606060",
                    fontStyle: "italic",
                    textDecoration: "none"
                }
            }
        }
    }
};

const redTheme: ThemeOptions = {
    palette: {
        primary: {
            main: "#b71c1c",
            dark: "#D0021B",
            light: "#c54949",
        },
        secondary: orange,
    },

    genie: {
        font: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 300,
            lineHeight: "1.5em"
        },

        boxShadow: {
            default: {
                border: "0",
                borderRadius: "3px",
                boxShadow: "0 10px 20px -12px rgba(0, 0, 0, 0.42), 0 3px 20px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2)",
                padding: "10px 0",
                transition: "all 150ms ease 0s"
            },

            primary: "0 12px 20px -10px rgba(39, 98, 176, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(39, 75, 176, 0.2)",

            secondary: "none",

            error: "0 12px 20px -10px rgba(244, 67, 54, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(244, 67, 54, 0.2)"
        },

        palette: {
            pressed: {
                main: blue[400],
                dark: blue[600],
                light: blue[200],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(255, 152, 0, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(255, 152, 0, 0.2)"
            },

            notice: {
                main: "#FFF8E5",
                dark: "#FFF2CC",
                light: "#FFFAEB",
                contrastText: "#5B9BD5",
                boxShadow: "0 12px 20px -10px rgba(255, 230, 160, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(255, 230, 160, 0.2)"
            },

            warning: {
                main: orange[500],
                dark: amber[900],
                light: orange[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(255, 152, 0, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(255, 152, 0, 0.2)"

            },
            info: {
                main: blue[500],
                dark: blue[900],
                light: blue[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(0, 188, 212, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(0, 188, 212, 0.2)"
            },
            success: {
                main: green[500],
                dark: green[900],
                light: green[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(76, 175, 80, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(76, 175, 80, 0.2)"
            },
            muted: {
                main: grey[500],
                dark: grey[900],
                light: grey[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(76, 175, 80, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(76, 175, 80, 0.2)"
            },
        },

        sideMenu: {
            activeTextColor: "#fff",
            backgroundColor: "#f7b74f",
            borderLeftColor: "#F5A623",
            floatParcialRouteTextColor: "#f7b74f",
            floatBackgroundColor: "#F5A623",
            linkColor: orange[500],
            textColor: "#7f7f7f",
        },

        icon: {
            action: {
                color: "#909090",
            },
        },


        user: {
            ordinary: {
                styles: {
                    color: "#808080",
                    fontWeight: "bold"
                }
            },

            sysUser: {
                styles: {
                    color: "#606060",
                    fontStyle: "italic"
                }
            }
        }

    }
};

const greenTheme: ThemeOptions = {
    palette: {
        primary: {
            main: "#336666",
            dark: "#2E9A95",
            light: "#78B64B",
        },

        secondary: {
            light: "#333F50",
            main: darken("#333F50", 0.3),
            dark: darken("#333F50", 0.6),
        },
    },

    genie: {
        font: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 300,
            lineHeight: "1.5em"
        },

        boxShadow: {

            default: {
                border: 0,
                borderRadius: 3,
                boxShadow: "0 10px 20px -12px rgba(0, 0, 0, 0.42), 0 3px 20px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2)",
                padding: "10px 0",
                transition: "all 150ms ease 0s"
            },

            primary: "0 12px 20px -10px rgba(39, 98, 176, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(39, 75, 176, 0.2)",

            secondary: "none",

            error: "0 12px 20px -10px rgba(244, 67, 54, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(244, 67, 54, 0.2)",
        },

        palette: {
            pressed: {
                main: blue[400],
                dark: blue[600],
                light: blue[200],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(255, 152, 0, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(255, 152, 0, 0.2)"
            },

            notice: {
                main: "#FFF8E5",
                dark: "#FFF2CC",
                light: "#FFFAEB",
                contrastText: "#5B9BD5",
                boxShadow: "0 12px 20px -10px rgba(255, 230, 160, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(255, 230, 160, 0.2)"
            },

            warning: {
                main: orange[400],
                dark: orange[600],
                light: orange[200],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(255, 152, 0, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(255, 152, 0, 0.2)"

            },
            info: {
                main: blue[500],
                dark: blue[900],
                light: blue[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(0, 188, 212, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(0, 188, 212, 0.2)"
            },
            success: {
                main: green[500],
                dark: green[900],
                light: green[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(76, 175, 80, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(76, 175, 80, 0.2)"
            },
            muted: {
                main: grey[500],
                dark: grey[900],
                light: grey[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(76, 175, 80, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(76, 175, 80, 0.2)"
            },
        },

        sideMenu: {
            activeTextColor: "#fff",
            backgroundColor: "#77B748",
            borderLeftColor: "#BECB3C",
            floatParcialRouteTextColor: "#21AF76",
            floatBackgroundColor: "#3F7F47",
            linkColor: green[500],
            textColor: "#7f7f7f",
        },

        icon: {
            action: {
                color: "#909090",
            },
        },

        user: {
            ordinary: {
                styles: {
                    color: "#808080",
                    fontWeight: "bold",
                    textDecoration: "none"
                }
            },

            sysUser: {
                styles: {
                    color: "#606060",
                    fontStyle: "italic",
                    textDecoration: "none"
                }
            }
        }
    }
};

const lightBlueTheme: ThemeOptions = {
    palette: {
        primary: {
            main: "#306CDC",
            dark: "#0042A9",
            light: "#718AFF",
        },

        secondary: {
            main: "#3399FF",
            dark: "#006CCB",
            light: "#79C9FF",
        },
    },

    genie: {
        font: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 300,
            lineHeight: "1.5em"
        },

        boxShadow: {

            default: {
                border: 0,
                borderRadius: 3,
                boxShadow: "0 10px 20px -12px rgba(0, 0, 0, 0.42), 0 3px 20px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2)",
                padding: "10px 0",
                transition: "all 150ms ease 0s"
            },

            primary: "0 12px 20px -10px rgba(39, 98, 176, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(39, 75, 176, 0.2)",

            secondary: "none",

            error: "0 12px 20px -10px rgba(244, 67, 54, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(244, 67, 54, 0.2)",
        },

        palette: {
            pressed: {
                main: blue[400],
                dark: blue[600],
                light: blue[200],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(255, 152, 0, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(255, 152, 0, 0.2)"
            },

            notice: {
                main: "#FFF8E5",
                dark: "#FFF2CC",
                light: "#FFFAEB",
                contrastText: "#5B9BD5",
                boxShadow: "0 12px 20px -10px rgba(255, 230, 160, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(255, 230, 160, 0.2)"
            },

            warning: {
                main: orange[400],
                dark: orange[600],
                light: orange[200],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(255, 152, 0, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(255, 152, 0, 0.2)"

            },
            info: {
                main: blue[500],
                dark: blue[900],
                light: blue[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(0, 188, 212, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(0, 188, 212, 0.2)"
            },
            success: {
                main: green[500],
                dark: green[900],
                light: green[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(76, 175, 80, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(76, 175, 80, 0.2)"
            },
            muted: {
                main: grey[500],
                dark: grey[900],
                light: grey[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(76, 175, 80, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(76, 175, 80, 0.2)"
            },
        },

        sideMenu: {
            activeTextColor: "#fff",
            backgroundColor: "#000051",
            borderLeftColor: "#42A5F5",
            floatParcialRouteTextColor: "#21AF76",
            floatBackgroundColor: "#01579b",
            linkColor: blue[500],
            textColor: "#7f7f7f",
        },

        icon: {
            action: {
                color: "#909090",
            },
        },

        user: {
            ordinary: {
                styles: {
                    color: "#808080",
                    fontWeight: "bold",
                    textDecoration: "none"
                }
            },

            sysUser: {
                styles: {
                    color: "#606060",
                    fontStyle: "italic",
                    textDecoration: "none"
                }
            }
        }
    }
};

const darkBlueTheme: ThemeOptions = {
    palette: {
        primary: {
            main: "#20245c",
            dark: "#222165",
            light: "#141546",
        },

        secondary: {
            main: "#3399FF",
            dark: "#006CCB",
            light: "#79C9FF",
        },
    },

    genie: {
        font: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 300,
            lineHeight: "1.5em"
        },

        boxShadow: {

            default: {
                border: 0,
                borderRadius: 3,
                boxShadow: "0 10px 20px -12px rgba(0, 0, 0, 0.42), 0 3px 20px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2)",
                padding: "10px 0",
                transition: "all 150ms ease 0s"
            },

            primary: "0 12px 20px -10px rgba(39, 98, 176, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(39, 75, 176, 0.2)",

            secondary: "none",

            error: "0 12px 20px -10px rgba(244, 67, 54, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(244, 67, 54, 0.2)",
        },

        palette: {
            pressed: {
                main: blue[400],
                dark: blue[600],
                light: blue[200],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(255, 152, 0, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(255, 152, 0, 0.2)"
            },

            notice: {
                main: "#FFF8E5",
                dark: "#FFF2CC",
                light: "#FFFAEB",
                contrastText: "#5B9BD5",
                boxShadow: "0 12px 20px -10px rgba(255, 230, 160, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(255, 230, 160, 0.2)"
            },

            warning: {
                main: orange[400],
                dark: orange[600],
                light: orange[200],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(255, 152, 0, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(255, 152, 0, 0.2)"

            },
            info: {
                main: blue[500],
                dark: blue[900],
                light: blue[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(0, 188, 212, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(0, 188, 212, 0.2)"
            },
            success: {
                main: green[500],
                dark: green[900],
                light: green[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(76, 175, 80, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(76, 175, 80, 0.2)"
            },
            muted: {
                main: grey[500],
                dark: grey[900],
                light: grey[300],
                contrastText: "#fff",
                boxShadow: "0 12px 20px -10px rgba(76, 175, 80, 0.28), 0 4px 20px 0px rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(76, 175, 80, 0.2)"
            },
        },

        sideMenu: {
            activeTextColor: "#fff",
            backgroundColor: "#000051",
            borderLeftColor: "#42A5F5",
            floatParcialRouteTextColor: "#21AF76",
            floatBackgroundColor: "#353c7c",
            linkColor: blue[500],
            textColor: "#7f7f7f",
        },

        icon: {
            action: {
                color: "#909090",
            },
        },

        user: {
            ordinary: {
                styles: {
                    color: "#808080",
                    fontWeight: "bold",
                    textDecoration: "none"
                }
            },

            sysUser: {
                styles: {
                    color: "#606060",
                    fontStyle: "italic",
                    textDecoration: "none"
                }
            }
        }
    }
};

export const appThemes = {
    "default": defaultTheme,
    "red": redTheme,
    "green": greenTheme,
    "lightBlue": lightBlueTheme,
    "darkBlue": darkBlueTheme,
};

export const appThemeNames = {
    "default": {
        en: "Default Theme",
        es: "Tema Padrón",
        pt: "Tema Padrão"
    },
    "red": {
        en: "Red Theme",
        es: "Tema Rojo",
        pt: "Tema Vermelho"
    },
    "green": {
        en: "Green Theme",
        es: "Tema Verde",
        pt: "Tema Verde"
    },
    "lightBlue": {
        en: "Light Blue Theme",
        es: "Tema Azul Claro",
        pt: "Tema Azul Claro"
    },
    "darkBlue": {
        en: "Dark Blue Theme",
        es: "Tema Azul Oscuro",
        pt: "Tema Azul Escuro"
    },
};

const appMainTheme = (genieTheme: string, uiCulture: string) => {

    if (!appThemes[genieTheme])
        genieTheme = "default";

    const culture = uiCulture.replace('-', '');

    return createTheme(
        {
            typography: {
                useNextVariants: true,
                h6: {
                    textTransform: "uppercase"
                },
                h5: {
                    lineHeight: "1.7rem"
                }
            },

            overrides: {
                MuiFormLabel: {
                    root: { fontWeight: "bold" }
                },
                MuiTabs: {
                    indicator: {
                        backgroundColor: red["900"],
                        height: "4px"
                    }
                }
            },

            components: {
                MuiTextField: {
                    defaultProps: {
                        variant: "standard"
                    }
                },
                MuiLink: {
                    defaultProps: {
                        underline: 'hover'
                    }
                },
                MuiInputLabel: {
                    defaultProps: {
                        variant: "standard"
                    }
                },
                MuiSelect: {
                    defaultProps: {
                        variant: "standard"
                    }
                },
                MuiCssBaseline: {
                    styleOverrides: {
                        body: {
                            fontSize: '0.875rem',
                            lineHeight: 1.43,
                            letterSpacing: '0.01071em',
                        },
                    }
                }
            },

            ...appThemes[genieTheme]
        },
        locales[culture]
    );
};

export default appMainTheme;
